import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, useTheme } from '@mui/material';
import { downloadNodeOutputDataFile, getNodeOutputData } from 'api/workflows/requests';
import CloseButton from 'components/button/CloseButton';
import { useState } from 'react';
import { FaDownload, FaTable } from 'react-icons/fa';
import { PreviewTable, PreviewTableWrapper, PreviewTriggerButton } from './WorkflowExecutionPreviewDataButton.styles';

export interface WorkflowExecutionPreviewDataButtonProps {
  /** Workflow execution identifier used to fetch preview and download file */
  executionId: string;
  /** Selected node identifier inside the workflow */
  nodeId: string;
  /** Max size of text for each cell */
  previewSize?: number;
}

/**
 * Renders a button that opens a dialog with a small data preview (table)
 * of a node's output from a specific workflow execution. Also provides
 * a "Download" action with a simple progress indicator.
 */
export default function WorkflowExecutionPreviewDataButton({
  executionId,
  nodeId,
  previewSize = 100,
}: WorkflowExecutionPreviewDataButtonProps) {
  const theme = useTheme();
  type PreviewRow = Record<string, string | number | boolean | null>;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [resultDownloadProgress, setResultDownloadProgress] = useState(0);

  const handlePreview = async () => {
    if (!executionId || !nodeId) return;

    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const result = await getNodeOutputData({
        workflowExecutionId: executionId,
        nodeId,
        pageParam: 1,
        pageSize: 20,
        sortColumn: 'giq_index',
        sortDirection: '',
        previewSize,
      });
      setPreviewData(result.data ?? []);
    } catch (err: unknown) {
      setPreviewData([]);
      const errorMsg = err instanceof Error ? err.message : String(err);
      setPreviewError(`Failed to load preview. ${errorMsg} for execution: "${executionId}"`);
    } finally {
      setPreviewLoading(false);
      setPreviewOpen(true);
    }
  };

  return (
    <>
      <div className="text-right">
        <PreviewTriggerButton
          variant="outlined"
          color="info"
          size="small"
          onClick={() => void handlePreview()}
          disabled={previewLoading}
          startIcon={previewLoading ? <CircularProgress size={16} /> : <FaTable size={13} />}
        >
          {previewLoading ? 'Loading…' : <>Preview output</>}
        </PreviewTriggerButton>
        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="xl" fullWidth>
          <DialogTitle>
            <div className="flex">
              <div className="text-xl font-bold">Preview Output Data</div>
              <div className="flex-1">
                <Box sx={{ flexGrow: 1 }}>
                  {resultDownloadProgress > 0 && resultDownloadProgress < 100 && (
                    <Box sx={{ width: '100%' }}>
                      <div style={{ fontSize: 12, marginBottom: 4 }}>Downloading: {resultDownloadProgress}%</div>
                      <div style={{ background: theme.palette.divider, height: 6, borderRadius: 3 }}>
                        <div
                          style={{
                            width: `${resultDownloadProgress}%`,
                            height: '100%',
                            background: theme.palette.primary.main,
                            borderRadius: 3,
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                    </Box>
                  )}
                </Box>
              </div>
              <div className="flex flex-row gap-2 items-center">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FaDownload />}
                  disabled={previewError != null || !executionId || !nodeId}
                  onClick={() => {
                    setResultDownloadProgress(0); // reset before download starts
                    void downloadNodeOutputDataFile({
                      workflowExecutionId: executionId ?? '',
                      nodeId: nodeId ?? '',
                      setProgress: setResultDownloadProgress,
                    });
                  }}
                >
                  Download
                </Button>
                <CloseButton onClose={() => setPreviewOpen(false)} />
              </div>
            </div>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {previewError ? (
              <div style={{ color: theme.palette.error.main, padding: '1rem' }}>{previewError}</div>
            ) : previewData.length === 0 ? (
              <div style={{ padding: '1rem', color: theme.palette.text.secondary }}>No data available.</div>
            ) : (
              <PreviewTableWrapper>
                <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                  <PreviewTable>
                    <thead>
                      <tr>
                        {Object.keys(previewData[0] ?? {}).map((key) => (
                          <th key={key}>{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {Object.values(row as Record<string, string>).map((value, colIndex) => (
                            <td key={colIndex} className="whitespace-nowrap overflow-hidden text-ellipsis">
                              {String(value)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </PreviewTable>
                </Box>
              </PreviewTableWrapper>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
